import { ApiError, api } from './api.js';
import { db, type OutboxItem } from './db.js';

const MAX_ATTEMPTS = 5;

let flushing = false;

function isValidItem(it: OutboxItem): boolean {
  return (
    Number.isInteger(it.session_id) &&
    it.session_id > 0 &&
    Number.isInteger(it.exercise_id) &&
    it.exercise_id > 0 &&
    Number.isInteger(it.set_number) &&
    it.set_number > 0 &&
    Number.isFinite(it.weight_kg) &&
    it.weight_kg >= 0 &&
    Number.isInteger(it.reps) &&
    it.reps > 0 &&
    typeof it.client_uuid === 'string' &&
    it.client_uuid.length > 0
  );
}

export async function enqueueSet(item: Omit<OutboxItem, 'id' | 'created_at' | 'attempts'>) {
  const candidate: OutboxItem = { ...item, created_at: Date.now(), attempts: 0 };
  if (!isValidItem(candidate)) {
    // Don't poison the queue. The UI already validates earlier; this is a
    // belt-and-suspenders against bad state.
    console.warn('[outbox] dropping invalid set', candidate);
    return;
  }
  await db.outbox.add(candidate);
  void flush();
}

export async function flush(): Promise<void> {
  if (flushing) return;
  if (!navigator.onLine) return;
  flushing = true;
  try {
    const items = await db.outbox.orderBy('created_at').toArray();
    for (const it of items) {
      // Toxic item from an older buggy build (e.g. session_id = NaN).
      // Server would 400/22P02 on every retry — drop it.
      if (!isValidItem(it) || it.attempts >= MAX_ATTEMPTS) {
        if (it.id !== undefined) await db.outbox.delete(it.id);
        continue;
      }
      try {
        await api.post(`/sessions/${it.session_id}/sets`, {
          exercise_id: it.exercise_id,
          set_number: it.set_number,
          weight_kg: it.weight_kg,
          reps: it.reps,
          rpe: it.rpe,
          client_uuid: it.client_uuid,
        });
        if (it.id !== undefined) await db.outbox.delete(it.id);
      } catch (err) {
        // 4xx from server means the payload itself is unsalvageable —
        // bumping attempts and retrying forever is pointless.
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          if (it.id !== undefined) await db.outbox.delete(it.id);
          continue;
        }
        if (it.id !== undefined) {
          await db.outbox.update(it.id, { attempts: it.attempts + 1 });
        }
        // Stop on transient failure; will retry next tick.
        break;
      }
    }
  } finally {
    flushing = false;
  }
}

export async function clearOutbox(): Promise<number> {
  const n = await db.outbox.count();
  await db.outbox.clear();
  return n;
}

export function startOutboxLoop() {
  window.addEventListener('online', () => void flush());
  // Best-effort periodic flush.
  setInterval(() => void flush(), 15_000);
  void flush();
}

export async function pendingCount(): Promise<number> {
  return db.outbox.count();
}
